import type { ViewportTier } from '../design-system/viewport-controller.js';

/**
 * <capability-registry> — Responsive Workstream B (roam-responsive-pwa-
 * spec.md §7). ONE typed, code-defined map of route/surface → per-tier
 * capability. No component decides `if (isMobile)` on its own — every
 * gating decision in the app reads this file, so there is exactly one
 * place that answers "does this work here, and if not, why".
 *
 * Levels (spec §7):
 *   - full       — works, certified
 *   - degraded   — works, not yet certified (a caveat, not a wall)
 *   - view-only  — the read half works; the write/edit half doesn't
 *   - blocked    — genuinely unusable at this tier; show the gate panel
 *
 * Levels are declared INDEPENDENTLY per tier (spec §1.12) — "blocked on
 * compact, degraded on medium" is the expected shape (Broadcast's show
 * designer is the canonical example), not an edge case. An entry may
 * omit a tier entirely; an omitted tier — like an entirely unlisted
 * route — defaults to 'full'. Unlisted is the common case: this
 * registry only carries what's genuinely gated.
 *
 * TWO KINDS OF PATTERN, one resolver:
 *   - a REAL router path ('/admin/schema-explorer') — matched against
 *     the current URL by the shell-level router guard (app.ts's
 *     `_shell`), which renders <cos-needs-desktop> for the WHOLE route.
 *   - a LOGICAL surface key with no route of its own
 *     ('/broadcast/designer' — the designer is a MODE inside the single
 *     `/broadcast` page, not a URL) — consulted DIRECTLY by the owning
 *     component (e.g. cos-broadcast-page calls
 *     `capabilityFor('/broadcast/designer', tier)` before opening the
 *     canvas) so a same-route, different-MODE gate still goes through
 *     this one registry and this one resolver. Documented per entry
 *     below; never invent a THIRD mechanism for this.
 *
 * Route matching is longest-prefix, so a more specific entry always
 * wins over a broader one registered earlier or later — order in this
 * array does not matter.
 */

export type CapabilityLevel = 'full' | 'degraded' | 'view-only' | 'blocked';

const TIER_ORDER: ViewportTier[] = ['compact', 'medium', 'expanded'];

interface CapabilityDecl {
	/** Route prefix, or a logical (non-navigable) surface key — see the
	 *  module doc above. */
	pattern: string;
	/** Short, human name for the gated surface. Feeds the gate panel
	 *  headline/banner copy and the nav marker tooltip. */
	label: string;
	/** Per-tier level. An absent tier defaults to 'full'. */
	levels: Partial<Record<ViewportTier, CapabilityLevel>>;
	/** Per-tier reason shown in the gate panel/banner. Optional — a
	 *  generic sentence covers an entry with no note. */
	notes?: Partial<Record<ViewportTier, string>>;
}

export interface CapabilityResult {
	level: CapabilityLevel;
	/** Present only when a registry entry matched (i.e. level may still
	 *  be 'full' for an entry whose OTHER tiers are gated). */
	label?: string;
	note?: string;
	/** The matched entry's own pattern — callers that need to look up
	 *  the entry again (e.g. for `deviceHandoffLabel`) reuse this rather
	 *  than re-deriving it. */
	pattern?: string;
}

/**
 * The seed registry — small and honest (spec §7's provisional tier
 * map), not an attempt to gate everything up front. Phase 0 usage data
 * (gate-hit telemetry, §17) is what grows this list; a route with no
 * entry here is simply not gated yet, not "certified full" by fiat.
 */
export const CAPABILITY_REGISTRY: CapabilityDecl[] = [
	// The canonical case (spec §1.12 round 4, §7): a drag-and-drop show
	// canvas is genuinely unusable on a phone and workable-with-care on a
	// tablet. LOGICAL key — `/broadcast` is one route with a `_tab`/
	// designer-open internal state; cos-broadcast-page consults this
	// pattern directly before rendering the canvas.
	{
		pattern: '/broadcast/designer',
		label: 'Broadcast show designer',
		levels: { compact: 'blocked', medium: 'degraded' },
		notes: {
			compact: 'The show designer is a drag-and-drop canvas: it needs a tablet or larger screen.',
			medium: 'Touch drag on the designer canvas is not yet certified here. It may work, but double-check layouts on a desktop before you push a show live.',
		},
	},
	// Roster's six-space MANAGER shell is a week grid, a half-hour day
	// board and a beside-content panel: `cos-roster-shell` declares
	// `min-width: 1180px` because that is what those surfaces need. The
	// WORKER view at the same route is a different component
	// (`rs-worker`) built for a phone and verified there, which is why
	// this is a LOGICAL key the roster page consults before choosing the
	// manager shell, not a route gate — gating `/roster` itself would
	// wrongly block the staff member holding the phone. Without this
	// entry the manager shell defaulted to `full` at every tier by
	// omission, which was never true.
	{
		pattern: '/roster/manager',
		label: 'Roster planning',
		levels: { compact: 'blocked', medium: 'degraded' },
		notes: {
			compact: 'Building and publishing a roster needs the week grid and the day board, which need a tablet or larger screen. Your own roster, shifts, leave and swaps all work here.',
			medium: 'The week grid and day board fit, but they are tight: check a published week on a desktop before staff see it.',
		},
	},
	// Promoting a column runs live DDL against production (Stage 47e) —
	// reserved for a desktop regardless of tier; browsing the existing
	// mappings is safe anywhere. Real routes.
	{
		pattern: '/admin/schema-explorer',
		label: 'Schema explorer',
		levels: { compact: 'view-only', medium: 'view-only' },
		notes: {
			compact: 'Promoting a column runs live schema changes: that action is reserved for a desktop. You can browse the mappings here.',
			medium: 'Promoting a column runs live schema changes: that action is reserved for a desktop. You can browse the mappings here.',
		},
	},
	{
		pattern: '/admin/data-console',
		label: 'Data console',
		levels: { compact: 'view-only', medium: 'view-only' },
		notes: {
			compact: 'Proposing or confirming a data change is reserved for a desktop. You can browse past changes here.',
			medium: 'Proposing or confirming a data change is reserved for a desktop. You can browse past changes here.',
		},
	},
	// LOGICAL key — dashboard layout EDIT mode, not a route (viewing a
	// dashboard at /dashboards/:id stays full on every tier; only the
	// drag-to-arrange edit mode degrades). Consulted by the dashboard
	// grid before it turns on `alwaysEditable`/edit-mode chrome.
	{
		pattern: '/dashboards/edit-mode',
		label: 'Dashboard layout editing',
		levels: { compact: 'degraded' },
		notes: {
			compact: 'Drag-to-arrange is cramped on a phone: resizing and moving tiles between zones work better on a tablet or larger. Viewing the dashboard is unaffected.',
		},
	},
];

// '/desk' (Desk, docs/desk-plan.md §7) is DELIBERATELY UNLISTED here —
// not an oversight, a registered decision. Desk is plausibly the single
// best Roam surface (a librarian on the floor with a tablet, helping
// someone away from the counter), so it must work FULL at every tier:
// compact, medium and expanded alike. An unlisted pattern already
// resolves to 'full' everywhere (see capabilityFor below and its own
// test), so the correct way to register "explicitly not degraded, not
// desktop-only" is to name the decision here rather than add an entry
// this registry's own docstring says only exists for genuinely gated
// surfaces. Don't add a Desk entry unless a REAL degradation is found —
// that would contradict this registered decision, not extend it.

function normalizePath(path: string): string {
	const stripped = path.split('?')[0]?.split('#')[0] ?? '';
	if (stripped.length > 1 && stripped.endsWith('/')) return stripped.slice(0, -1);
	return stripped;
}

function patternMatches(path: string, pattern: string): boolean {
	return path === pattern || path.startsWith(pattern + '/');
}

function findEntry(path: string): CapabilityDecl | null {
	const normalized = normalizePath(path);
	let best: CapabilityDecl | null = null;
	for (const decl of CAPABILITY_REGISTRY) {
		if (!patternMatches(normalized, decl.pattern)) continue;
		if (!best || decl.pattern.length > best.pattern.length) best = decl;
	}
	return best;
}

/** Pure resolver — the ONE function every gating call site (router-level
 *  or component-level) consults. No DOM, no matchMedia; feed it a path
 *  (real or logical) and a tier from ViewportController. */
export function capabilityFor(path: string, tier: ViewportTier): CapabilityResult {
	const entry = findEntry(path);
	if (!entry) return { level: 'full' };
	return {
		level: entry.levels[tier] ?? 'full',
		label: entry.label,
		note: entry.notes?.[tier],
		pattern: entry.pattern,
	};
}

/** The smallest viewport tier at which `pattern` is NOT blocked — feeds
 *  the gate panel's device-aware headline ("this needs a tablet or
 *  larger" vs "...a desktop") without hand-authoring the sentence per
 *  registry entry. Returns 'compact' for an unlisted/unknown pattern
 *  (nothing blocks it) and null only if every declared tier, including
 *  'expanded', is blocked (nothing in the seed registry goes that far —
 *  guarded so a future entry that DOES can't silently mislabel itself). */
export function smallestWorkingTier(pattern: string): ViewportTier | null {
	const entry = CAPABILITY_REGISTRY.find((d) => d.pattern === pattern);
	if (!entry) return 'compact';
	for (const tier of TIER_ORDER) {
		if ((entry.levels[tier] ?? 'full') !== 'blocked') return tier;
	}
	return null;
}

/** The device-handoff phrase for a blocked pattern's gate panel — "a
 *  tablet or larger screen" when medium works, "a desktop" when only
 *  expanded works, and a generic fallback for the (currently
 *  unreachable) everything-blocked case. */
export function deviceHandoffLabel(pattern: string): string {
	switch (smallestWorkingTier(pattern)) {
		case 'medium':
			return 'a tablet or larger screen';
		case 'expanded':
			return 'a desktop';
		default:
			return 'a larger screen';
	}
}
